import React from 'react';
import {
  Avatar,
  Typography,
  Autocomplete,
  Chip
} from '@axtria/design-system-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRightFromBracket,
  faBuilding,
  faChevronDown,
  faChevronUp,
  faKeynote,
  faPencil,
  faRepeat
} from '@fortawesome/pro-light-svg-icons';
import _ from 'lodash';
import './ProfileSwitch.scss';
import { useProfileSwitch } from './useProfileSwitch';
import { LOGOUT, COMPANY, WORKSPACE, SWITCH_VIEW, WORKSPACE_OWNER } from '../../../constants/Constantsi18';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SET_USER_INFO_KEY_VALUE } from '../../../actions/app.type';

interface ProfileSwitchProps {
  onClose?: () => void;
}

const normalizeLabel = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);

  return value.name
    ?? value.label
    ?? value.brand_name
    ?? value.brandName
    ?? value.project_name
    ?? value.profileName
    ?? '';
};

const normalizeList = (items: any): string[] => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item: any) => normalizeLabel(item))
    .filter(Boolean);
};

const selectList = (selectedItems: any, fallbackItems: any): string[] => {
  if (selectedItems !== undefined && selectedItems !== null) {
    return normalizeList(selectedItems);
  }

  return normalizeList(fallbackItems);
};

const firstLabel = (...values: any[]): string => (
  values.map((value) => normalizeLabel(value)).find(Boolean) || ''
);

const UserInfoSection = ({ loginData }: any) => (
  <div className="user-info-section">
    <Avatar className="user-info-avatar" alt="" size="large">
      {loginData.first_name
        ? _.upperFirst(loginData.first_name.charAt(0))
        : loginData.username && _.upperFirst(loginData.username.charAt(0))}
      {loginData.last_name ? _.upperFirst(loginData.last_name.charAt(0)) : ''}
    </Avatar>

    <div className="user-info-name">
      <Typography level="subtitle" levelVariant="small500" textColor="grey.70">
        {loginData?.first_name} {loginData?.last_name}
      </Typography>
    </div>
  </div>
);

const SwitchViewSection = ({ loginData, profileShowData, changeProfile }: any) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!profileShowData || profileShowData.length <= 1) return null;

  const isConfigurator = (item: any) => item?.name?.trim().toLowerCase() === WORKSPACE_OWNER.toLowerCase();
  const configuratorItem = profileShowData.find(isConfigurator);
  const userItem = profileShowData.find((item: any) => !isConfigurator(item));

  const handleSelect = (e: React.MouseEvent, item: any) => {
    if (!item || loginData?.preferredProfile === item.id) return;
    changeProfile(e as any, item.id, profileShowData);
  };

  return (
    <div className="switch-view-wrapper">
      <div
        className="switch-view-row"
        onClick={() => setExpanded((prev: boolean) => !prev)}
      >
        <FontAwesomeIcon className="switch-view-icon" icon={faRepeat as IconProp} />
        <Typography level="ui" levelVariant="medium500" textColor="grey.70">
          {SWITCH_VIEW}
        </Typography>
        <FontAwesomeIcon
          className="switch-view-chevron"
          icon={(expanded ? faChevronUp : faChevronDown) as IconProp}
        />
      </div>

      {expanded && (
        <div className="view-toggle">
          <button
            type="button"
            className={`view-toggle-option${loginData?.preferredProfile === userItem?.id ? ' active' : ''}`}
            disabled={!userItem}
            onClick={(e) => handleSelect(e, userItem)}
          >
            User View
          </button>
          <button
            type="button"
            className={`view-toggle-option${loginData?.preferredProfile === configuratorItem?.id ? ' active' : ''}`}
            disabled={!configuratorItem}
            onClick={(e) => handleSelect(e, configuratorItem)}
          >
            Configurator View
          </button>
        </div>
      )}
    </div>
  );
};

const CompanySection = ({ loginData, handleAutocompleteCompanyChange }: any) => {
  const options = loginData.company_data.map((item: any, index: number) => ({
    label: _.upperFirst(item.company_name),
    value: String(item.companyId),
    id: `${item.company_name.replace(/\s/g, '')}_${index}`,
  }));

  const selectedOption = options.filter(
    (opt: any) => opt.value === String(loginData.preferredCompany),
  );

  return (
    <div className="company-section">
      <div className="company-section-heading">
        <FontAwesomeIcon icon={faBuilding as IconProp} />
        <Typography level="ui" levelVariant="small500" textColor="grey.70">
          {COMPANY}
        </Typography>
      </div>
      <Autocomplete
        disablePortal
        className="company-autocomplete"
        dropdownOptions={options}
        listboxClassname="profile-autocomplete"
        defaultSelectedValue={selectedOption}
        refreshedSelectedList={selectedOption}
        placeholder="Search company..."
        onChange={(_e: any, value: any) => {
          const selected = Array.isArray(value) ? value[0] : value;
          if (selected?.value) {
            handleAutocompleteCompanyChange(Number(selected.value));
          }
        }}
      />
    </div>
  );
};

const WorkspaceSection = ({
  projectData,
  loginData,
  handleAutocompleteProjectChange,
}: any) => {
  const options = projectData.map((item: any, index: number) => ({
    label: item.project_name,
    value: String(item.project_id),
    id: `${item.project_name.replace(/\s/g, '')}_${index}`,
  }));

  const selectedOption = options.filter(
    (opt: any) => opt.value === String(loginData.preferredProject),
  );

  return (
    <div className="workspace-section" id="workspace">
      <div className="workspace-section-heading">
        <FontAwesomeIcon icon={faKeynote as IconProp} />
        <Typography level="ui" levelVariant="small500" textColor="grey.70">
          {WORKSPACE}
        </Typography>
      </div>
      <Autocomplete
        disablePortal
        className="workspace-autocomplete"
        listboxClassname="profile-autocomplete"
        dropdownOptions={options}
        defaultSelectedValue={selectedOption}
        refreshedSelectedList={selectedOption}
        placeholder="Search workspace..."
        onChange={(_e: any, value: any) => {
          const selected = Array.isArray(value) ? value[0] : value;
          if (selected?.value) {
            handleAutocompleteProjectChange(Number(selected.value));
          }
        }}
      />
    </div>
  );
};

const ObjectivesRow = ({ items }: { items: string[] }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const chipsRef = React.useRef<HTMLDivElement>(null);

  const CLAMPED_HEIGHT = 60;

  React.useLayoutEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    // Measure against the fixed clamp height, not el.clientHeight - clientHeight
    // reflects whatever class (clamped/expanded) is currently rendered, so
    // re-measuring while expanded would wrongly report no overflow and hide
    // the "See less" button.
    setIsOverflowing(el.scrollHeight > CLAMPED_HEIGHT + 1);
  }, [items, expanded]);

  return (
    <div className="profile-details-row">
      <Typography level="paragraph" levelVariant="overlineAllcaps">OBJECTIVES</Typography>
      <div
        ref={chipsRef}
        className={`profile-details-chips${expanded ? ' expanded' : ' clamped'}`}
      >
        {items.map((item: string, index: number) => (
          <Chip size="small" variant="primary" variantType="primary" key={`${item}-${index}`}>{item}</Chip>
        ))}
      </div>
      {isOverflowing && (
        <button
          type="button"
          className="profile-details-see-more"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
};

const ProfileDetailsSection = ({ loginData, onEditClick, canEdit }: any) => {
  const profileArray = Array.isArray(loginData?.profileData) ? loginData.profileData : [];
  const currentProfile = profileArray.find(
    (p: any) => p.id == loginData?.preferredProfile || p.name?.trim().toLowerCase() === String(loginData?.preferredProfile || '').trim().toLowerCase()
  );
  const currentProfileName = firstLabel(currentProfile, loginData?.preferredProfile);
  const isWorkspaceOwner = currentProfileName.trim().toLowerCase() === 'workspace owner';

  const currentCompany = loginData?.company_data?.find(
    (c: any) => c.companyId == loginData?.preferredCompany
  );
  const currentProject = currentCompany?.projectList?.find(
    (p: any) => p.project_id == loginData?.preferredProject
  );
  const isOnboardingEnabled = currentProject?.onboarding && currentProject?.onboarding !== false;

  if (isWorkspaceOwner || !isOnboardingEnabled) return null;

  const portfoliosForProject = (loginData?.userPortfolios || []).filter(
    (pf: any) => pf.project_id == loginData?.preferredProject
  );
  const portfolioItem = portfoliosForProject[0];

  const roleName = firstLabel(loginData?.selectedRole, portfolioItem?.profile, currentProfile);
  const portfolioBrands = portfoliosForProject.flatMap((pf: any) => pf.brands || []);
  const portfolioFunctions = portfoliosForProject.flatMap((pf: any) => pf.funcs || []);
  const portfolioObjectives = portfoliosForProject.flatMap((pf: any) => pf.goals || []);
  const brands = selectList(loginData?.selectedBrands, portfolioBrands);
  const functions = selectList(loginData?.selectedFunctions, portfolioFunctions);
  const objectives = selectList(loginData?.selectedGoals, portfolioObjectives);

  if (!roleName && brands.length === 0 && functions.length === 0 && objectives.length === 0) {
    return null;
  }

  const renderChips = (items: string[]) => (
    <div className="profile-details-chips">
      {items.map((item: string, index: number) => (
        <Chip size="small" variant="primary" variantType="primary" key={`${item}-${index}`}>{item}</Chip>
      ))}
    </div>
  );

  return (
    <div className="profile-details-wrapper" data-testid="profile-details-section">
      <div className="profile-details-heading">
        <Typography level="ui" levelVariant="medium500" textColor="grey.70">
          Profile Details
        </Typography>
        <button
          type="button"
          className={`profile-details-edit-button${canEdit ? '' : ' locked'}`}
          aria-label="Edit portfolio details"
          aria-disabled={!canEdit}
          disabled={!canEdit}
          onClick={canEdit ? onEditClick : undefined}
        >
          <FontAwesomeIcon icon={faPencil as IconProp} />
        </button>
      </div>

      <div className="profile-details-section">
        {roleName && (
          <div className="profile-details-row">
            <Typography level="paragraph" levelVariant="overlineAllcaps">ROLE</Typography>
            <Typography level="ui" levelVariant="medium400">{roleName}</Typography>
          </div>
        )}

        {brands.length > 0 && (
          <div className="profile-details-row">
            <Typography level="paragraph" levelVariant="overlineAllcaps">PORTFOLIO / BRAND</Typography>
            {renderChips(brands)}
          </div>
        )}

        {functions.length > 0 && (
          <div className="profile-details-row">
            <Typography level="paragraph" levelVariant="overlineAllcaps">FUNCTION</Typography>
            {renderChips(functions)}
          </div>
        )}

        {objectives.length > 0 && <ObjectivesRow items={objectives} />}
      </div>
    </div>
  );
};

const ProfileSwitch: React.FC<ProfileSwitchProps> = ({ onClose }) => {
  const {
    loginData,
    submitLogout,
    changeProfile,
    projectData,
    profileShowData,
    handleAutocompleteProjectChange,
    handleAutocompleteCompanyChange,
  } = useProfileSwitch(onClose);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleEditProfile = () => {
    dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'editProfileTriggered', value: true } });
    navigate('/');
    onClose?.();
  };

  const companyList = Array.isArray(loginData?.company_data) ? loginData.company_data : [];
  const projectList = Array.isArray(projectData) ? projectData : [];

  const showCompanySection = companyList.length > 0;
  const showWorkspaceSection = projectList.length > 0;
  const canEditProfileDetails = window.location.pathname === '/' && !!loginData?.isHomepageDashboard;

  return (
    <div id="ProfileSwitch">
      <UserInfoSection loginData={loginData} />

      <div className="profile-scroll-area">
        {showCompanySection ? (
          <CompanySection loginData={loginData} handleAutocompleteCompanyChange={handleAutocompleteCompanyChange} />
        ) : null}

        {showWorkspaceSection ? (
          <WorkspaceSection
            projectData={projectData}
            loginData={loginData}
            handleAutocompleteProjectChange={handleAutocompleteProjectChange}
          />
        ) : null}

        <ProfileDetailsSection
          loginData={loginData}
          onEditClick={handleEditProfile}
          canEdit={canEditProfileDetails}
        />
      </div>

      <SwitchViewSection
        loginData={loginData}
        profileShowData={profileShowData}
        changeProfile={changeProfile}
      />

      <div className="logout" onClick={submitLogout}>
        <FontAwesomeIcon className="logout-icon" icon={faArrowRightFromBracket as IconProp} />
        <Typography level="ui" levelVariant="medium500" textColor="grey.70">
          {LOGOUT}
        </Typography>
      </div>
    </div>
  );
};

export default ProfileSwitch;
